import React from "react";
import { TariffAccessBanner } from "../../components/TariffAccessBanner/TariffAccessBanner";
import { Shimmer } from "../../components/Shimmer/Shimmer";
import type { CheckDetailKind } from "./checkDetailKind";
import { useRequestDetailPage } from "./useRequestDetailPage";
import { RequestDetailTopBar } from "./components/RequestDetailTopBar";
import { RequestDetailInfoCard } from "./components/RequestDetailInfoCard";
import { RequestDetailTaskDeck } from "./components/RequestDetailTaskDeck";
import { RequestDetailScheduleModal } from "./components/RequestDetailScheduleModal";
import { RequestDetailRunsCalendarModal } from "./components/RequestDetailRunsCalendarModal";
import { RequestDetailTaskListSidebar } from "./components/RequestDetailTaskListSidebar";
import { RequestDetailChecksModal } from "./components/RequestDetailChecksModal";
import "../RequestDetail.scss";

export interface RequestDetailPageProps {
  requestId: string;
  kind?: CheckDetailKind;
}

export const RequestDetailPage: React.FC<RequestDetailPageProps> = ({
  requestId,
  kind = "site",
}) => {
  const page = useRequestDetailPage(requestId, kind);

  return (
    <div className="detail-page">
      <div className="detail-container">
        <RequestDetailTopBar
          loading={page.loading}
          data={page.data}
          isEditingRequest={page.isEditingRequest}
          requestEditLoading={page.requestEditLoading}
          scheduling={page.scheduling}
          scheduleDisabled={page.scheduleDisabled}
          isActionsMenuOpen={page.isActionsMenuOpen}
          canEditRequest={page.canEditRequest}
          hasSchedule={page.hasSchedule}
          canScanBySchedule={page.tariffAccess.canScanBySchedule}
          hasTaskItems={page.hasTaskItems}
          actionsMenuRef={page.actionsMenuRef}
          setIsActionsMenuOpen={page.setIsActionsMenuOpen}
          handleBack={page.handleBack}
          handleSaveRequestEdit={page.handleSaveRequestEdit}
          handleCancelRequestEdit={page.handleCancelRequestEdit}
          handleSchedule={page.handleSchedule}
          handleStartRequestEdit={page.handleStartRequestEdit}
          handleOpenChecksModal={page.handleOpenChecksModal}
          handleOpenScheduleModal={page.handleOpenScheduleModal}
          handleOpenTaskListModal={page.handleOpenTaskListModal}
        />

        {page.accessBanner ? (
          <TariffAccessBanner
            banner={page.accessBanner}
            blockReason={page.accessBlockReason}
            className="detail-tariff-access-banner"
          />
        ) : null}

        {page.loading ? (
          <Shimmer rows={3} />
        ) : !page.data ? (
          <div className="detail-card">
            <span className="detail-label">Заявка не найдена</span>
          </div>
        ) : (
          <>
            <RequestDetailInfoCard
              kind={kind}
              data={page.data}
              isEditingRequest={page.isEditingRequest}
              editCompanyName={page.editCompanyName}
              editInn={page.editInn}
              editUrl={page.editUrl}
              schedulePeriodicityLabel={page.schedulePeriodicityLabel}
              scheduleItem={page.scheduleItem}
              setEditCompanyName={page.setEditCompanyName}
              setEditInn={page.setEditInn}
              setEditUrl={page.setEditUrl}
              handleOpenRunsCalendar={page.handleOpenRunsCalendar}
            />

            <RequestDetailTaskDeck
              taskListLoading={page.taskListLoading}
              taskList={page.taskList}
              selectedTaskIndex={page.selectedTaskIndex}
              taskListCurrentPage={page.taskListCurrentPage}
              taskListTotalPages={page.taskListTotalPages}
              taskDetail={page.taskDetail}
              taskDetailLoading={page.taskDetailLoading}
              taskDetailsCache={page.taskDetailsCache}
              deckViewportWidth={page.deckViewportWidth}
              deckCardsRef={page.deckCardsRef}
              pdfContentRef={page.pdfContentRef}
              handlePrevTask={page.handlePrevTask}
              handleNextTask={page.handleNextTask}
              handleDownloadPdf={page.handleDownloadPdf}
            />
          </>
        )}
      </div>
      {page.isScheduleModalOpen && (
        <RequestDetailScheduleModal
          hasSchedule={page.hasSchedule}
          scheduleLoading={page.scheduleLoading}
          scheduleSaving={page.scheduleSaving}
          scheduleDeleting={page.scheduleDeleting}
          schedulePeriod={page.schedulePeriod}
          scheduleWeekDay={page.scheduleWeekDay}
          scheduleMonthDay={page.scheduleMonthDay}
          scheduleMonthLastDay={page.scheduleMonthLastDay}
          setSchedulePeriod={page.setSchedulePeriod}
          setScheduleWeekDay={page.setScheduleWeekDay}
          setScheduleMonthDay={page.setScheduleMonthDay}
          setScheduleMonthLastDay={page.setScheduleMonthLastDay}
          handleCloseScheduleModal={page.handleCloseScheduleModal}
          handleSaveSchedule={page.handleSaveSchedule}
          handleDeleteSchedule={page.handleDeleteSchedule}
        />
      )}
      {page.isRunsCalendarOpen && page.scheduleItem && (
        <RequestDetailRunsCalendarModal
          runsCalYm={page.runsCalYm}
          runsCalTitle={page.runsCalTitle}
          runsCalCells={page.runsCalCells}
          runsCalDaysWithRuns={page.runsCalDaysWithRuns}
          runsCalSelectedDay={page.runsCalSelectedDay}
          runsCalList={page.runsCalList}
          runsCalToday={page.runsCalToday}
          setRunsCalSelectedDay={page.setRunsCalSelectedDay}
          handleCloseRunsCalendar={page.handleCloseRunsCalendar}
          shiftRunsCalMonth={page.shiftRunsCalMonth}
        />
      )}
      <RequestDetailTaskListSidebar
        isTaskListModalOpen={page.isTaskListModalOpen}
        taskListSortDirection={page.taskListSortDirection}
        taskListModalItems={page.taskListModalItems}
        taskListModalLoading={page.taskListModalLoading}
        taskList={page.taskList}
        selectedTaskIndex={page.selectedTaskIndex}
        setTaskListSortDirection={page.setTaskListSortDirection}
        handleCloseTaskListModal={page.handleCloseTaskListModal}
        handleTaskListModalScroll={page.handleTaskListModalScroll}
        handleSelectTaskFromModal={page.handleSelectTaskFromModal}
      />
      {page.isChecksModalOpen && (
        <RequestDetailChecksModal
          kind={kind}
          handleCloseChecksModal={page.handleCloseChecksModal}
        />
      )}
    </div>
  );
};
